import Match from './Match.js'

/**
 * A Match key used to define what 'thing' is requesting the plugins
 */
const CLIENT = 'client'

/**
 * A Match key used to define what piece of the client the plugin will be used for
 */
const SCOPE = 'scope'

/**
 * A Match key used to define in what capacity the plugin will be used. 'menu', 'toolbar', etc
 */
const USE = 'use'

/**
 * A Match key used to define in what mode the plugin will be used in
 */
const MODE = 'mode'

/**
 * The key used in a plugin to define an object with match keys
 */
const MATCHES = 'matches'

/**
 * Holds the primary target of the context. Often this is the object
 * right clicked on, rolled-over, or has a more button next to it.
 * This can be an array
 */
const TARGET = 'target'

/**
 * In some cases, targets dont have a type. This can be used to define the
 * default target type
 */
const TARGET_TYPE = 'targetType'

/**
 * Holds the secondary target of the context. This can be an array
 */
const SECONDARY_TARGET = 'secondaryTarget'

/**
 * In some cases, secondaryTargets dont have a type. This can be used to define the
 * default secondaryTarget type
 */
const SECONDARY_TARGET_TYPE = 'secondaryTargetType'

// do we want these here???
const CONDITION_FACTORY = 'conditionFactory'
const SATISFIES_FACTORY = 'satisfiesFactory'
const PERFORMS_FACTORY = 'performsFactory'
const ACTION_FACTORY = 'actionFactory'
const IMAGINE_API = 'imagineApi'

/**
 * Returns true if the cxt matches the plugin's matches field
 */
function isMatch( cxt, plg ) {
  return Match.isMatch( cxt, plg[MATCHES] )
}

/**
 * Returns a function ( item ) that will invoke isMatch( cxt, item ).
 * This is useful for calling Plugins.get( type, createMatch( cxt ))
 **/
function createMatch( cxt ) {
  return item => isMatch( cxt, item )
}

/**
 * If cxt[type] is an array, this will return the zeroth element.
 * Otherwise, this will return cxt[type]
 */
function getObject( cxt, type ) {
  if ( Array.isArray( cxt[type] ) ) {
    return cxt[type][0]
  }
  return cxt[type]
}

/**
 * Returns the first target in the TARGET key
 */
function getTarget( cxt ) { return getObject( cxt, TARGET ) }

/**
 * Returns the first target in the SECONDARY_TARGET key
 */
function getSecondaryTarget( cxt ) { return getObject( cxt, SECONDARY_TARGET ) }

/**
 * If cxt[type] is an array, this will be returned.
 * Otherwise, this will return and array of containing 
 * the value at the type key (or an empty array if it is
 * null or undefined)
 */
function getObjects( cxt, type ) {
  if ( Array.isArray( cxt[type] ) ) {
    return cxt[type]
  }
  return cxt[type] == null ? [] : [ cxt[type] ]
}

/**
 * Returns the first target in the TARGET key
 */
function getTargets( cxt ) { return getObjects( cxt, TARGET ) }

/**
 * Returns the first target in the SECONDARY_TARGET key
 */
function getSecondaryTargets( cxt ) { return getObjects( cxt, SECONDARY_TARGET ) }

/**
 * Returns null, if no targets of the type are available. Otherwise this will return 
 * either the target or an array of objects that are of the specified type
 * @param {object|array} tgt the target(s) to evaluate
 * @param {string} type the expected type 
 * @param {string} [arrayCondition] if the tgt is an array, this controls
 * what is returned. Options are 'any', 'all', 'one' or 'none'. 
 * If 'any', an array of only the objects in the array meeting the type requirement
 * will be returned. If there are none, null is returned. 
 * If 'all', then * all object in the array must be of the type, or null 
 * will be returned. * If all are of the type, the tgt is returned.
 * If 'one', then only one object in the array must be of the type. that
 * object (not an array) will be returned.
 * * If 'onlyOne', then the array must be of length one and the object in the array 
 * must be of the type. That object (not an array) will be returned.
 * @param {boolean} asArray if true, any of the returns that would have been
 * a single object, it will be wrapped in an array
 */
function getOfType( tgt, type, options ) {
  if ( tgt == null ) {
    return null
  }
  const { arrayCondition = 'any', asArray = false, defaultType } = options
  const eql = ( obj ) => { return ( ( obj && obj.type ) || defaultType ) === type } /* Use Isa? */

  if ( Array.isArray( tgt ) ) {
    if ( arrayCondition === 'any' ) {
      const arr = tgt.filter( eql )
      return arr.length === 0 ? null : arr 
    }
    if ( arrayCondition === 'onlyOne' ) {
      if ( tgt.length !== 1 ) {
        return null
      }
      return eql( tgt[0] ) ? ( asArray ? tgt : tgt[0] ) : null
    } 
    if ( arrayCondition === 'one' ) {
      const arr = tgt.filter( eql )
      return arr.length === 1 ? ( asArray ? arr : arr[0] ) : null 
    } 

    if ( arrayCondition === 'all' ) {
      const notAll = tgt.some( obj => !eql( obj ) ) // Use Isa?
      return notAll ? null : tgt
    }
  }
  if ( eql( tgt ) ) { 
    return asArray ? [ tgt ] : tgt
  }
  return null
}

/**
 * Returns an array of type strings that are contained
 * in the tgt array
 */
function getTypes( tgt, defaultType ) {
  if ( tgt == null ) {
    return []
  }
  const set = new Set( )
  if ( Array.isArray( tgt ) ) {
    tgt.forEach( ( item ) => {
      const type = item.type || defaultType
      if ( type ) {
        set.add( type )
      }
    } )
    return Array.from( set )
  }
  return tgt.type ? [ tgt.type ] : []
}

/**
 * Returns an array of types contained in the target
 */
function getTargetTypes( cxt ) {
  return getTypes( cxt?.[TARGET], cxt?.[TARGET_TYPE] )
}

/**
 * This will return the object of the specified type at the TARGET key. 
 * If only is true, then TARGET must be an array of one or the object.
 * This is the same as getObjectOfType( cxt, TARGET, arrayCondition, false)
 * @param {object} cxt the context to retrieve target from
 * @param {string} type the type the object must be
 * @param {boolean} only if true, a target array must be of length one.
 * @return {object} the target or null if one is not found
 */
function getTargetOfType( cxt, type, only = true ) {
  return getOfType( cxt?.[TARGET], type, { 
    arrayCondition : only ? 'onlyOne' : 'one', 
    asArray : false, 
    defaultType : cxt?.[TARGET_TYPE] } )
}

/**
 * Returns true if getTargetOfType( ) does not return null
 */
function isTargetOfType( cxt, type, only = true ) { 
  return getTargetOfType( cxt, type, only ) !== null 
}

/**
 * This will return an array of objects of the specified type at the TARGET key,
 * or null if no objects are found. This is the same as 
 * getObjectOfType( cxt, TARGET, arrayCondition, false)
 * @param {object} cxt the context to retrieve target from
 * @param {string} type the type the object must be
 * @param {boolean} arrayCondition this defaults to 'any' and defines whether
 * all, any, one, or only one object must be of the type
 * @return {array} the objects of the specified type or null.
 */
function getTargetsOfType( cxt, type, arrayCondition = 'any' ) {
  return getOfType( cxt?.[TARGET], type, { 
    arrayCondition, 
    asArray : true, 
    defaultType : cxt?.[TARGET_TYPE]
  } )
}

/**
 * Returns true if getTargetsOfType( ) does not return null
 */
function hasTargetsOfType( cxt, type, arrayCondition = 'any' ) {
  return getTargetsOfType( cxt, type, arrayCondition ) != null
}

/**
 * Returns an array of types contained in the secondary target
 */
function getSecondaryTargetTypes( cxt ) {
  return getTypes( cxt?.[SECONDARY_TARGET], cxt?.[SECONDARY_TARGET_TYPE] )
}

/**
 * This will return the object of the specified type at the SECONDARY_TARGET key. 
 * If only is true, then TARGET must be an array of one or the object.
 * This is the same as getObjectOfType( cxt, SECONDARY_TARGET, arrayCondition, false)
 * @param {object} cxt the context to retrieve target from
 * @param {string} type the type the object must be
 * @param {boolean} only if true, a target array must be of length one.
 * @return {object} the target or null if one is not found
 */
function getSecondaryTargetOfType( cxt, type, only = true ) {
  return getOfType( cxt?.[SECONDARY_TARGET], type, { 
    arrayCondition : only ? 'onlyOne' : 'one', 
    asArray : false, 
    defaultType : cxt?.[SECONDARY_TARGET_TYPE] 
  } )
}

/**
 * Returns true if getTargetOfType( ) does not return null
 */
function isSecondaryTargetOfType( cxt, type, only = true ) { 
  return getSecondaryTargetOfType( cxt, type, only ) !== null 
}

/**
 * This will return an array of objects of the specified type at the SECONDARY_TARGET key,
 * or null if no objects are found. This is the same as 
 * getObjectOfType( cxt, SECONDARY_TARGET, arrayCondition, false)
 * @param {object} cxt the context to retrieve target from
 * @param {string} type the type the object must be
 * @param {boolean} arrayCondition this defaults to 'any' and defines whether
 * all, any, one, or only one object must be of the type
 * @return {array} the objects of the specified type or null.
 */
function getSecondaryTargetsOfType( cxt, type, arrayCondition = 'any' ) {
  return getOfType( cxt?.[SECONDARY_TARGET], type, { 
    arrayCondition, 
    asArray : true, 
    defaultType : cxt?.[SECONDARY_TARGET_TYPE]
  } )
}

/**
 * Returns true if getTargetsOfType( ) does not return null
 */
function hasSecondaryTargetsOfType( cxt, type, arrayCondition = 'any' ) {
  return getSecondaryTargetsOfType( cxt, type, arrayCondition ) != null
}

/**
 * Returns an array of types contained in the specified key of the context
 */
function getObjectTypes( cxt, key ) {
  return getTypes( cxt?.[key], cxt?.[`${key}Type`] )
}

/**
 * This will return the object of the specified type at the given key. 
 * If only is true, then TARGET must be an array of one or the object
 * @param {object} cxt the context to retrieve target from
 * @param {string} type the type the object must be
 * @param {boolean} only if true, a target array must be of length one.
 * @return {object} the target or null if one is not found
 */
function getObjectOfType( cxt, key, type, only = true ) {
  return getOfType( cxt?.[key], type, { 
    arrayCondition : only ? 'onlyOne' : 'one', 
    asArray : false, 
    defaultType : cxt?.[`${key}Type`]
  } )
}

/**
 * Returns true if getKeyOfType( ) does not return null
 */
function isObjectOfType( cxt, key, type, only = true ) { 
  return getObjectOfType( cxt, key, type, only ) !== null 
}

/**
 * This will return an array of objects of the specified type at the given key,
 * or null if no objects are found. The defaultType will be looked for under the
 * context's {key}Type field
 * @param {object} cxt the context to retrieve key from
 * @param {string} key the name of the property in the context
 * @param {string} type the type the object must be
 * @param {boolean} arrayCondition this defaults to 'any' and defines whether
 * all, any, one, or only one object must be of the type
 * @return {array} the objects of the specified type or null.
 */
function getObjectsOfType( cxt, key, type, arrayCondition = 'any' ) {
  return getOfType( cxt?.[key], type, { 
    arrayCondition, 
    asArray : true, 
    defaultType : cxt?.[`${key}Type`]
  } )    
}

/**
 * Returns true if getObjectsOfType( ) does not return null
 */
function hasObjectsOfType( cxt, key, type, arrayCondition = 'any' ) {
  return getObjectsOfType( cxt, key, type, arrayCondition ) != null
}

export default {
  isMatch,
  createMatch,
  getObject,
  getTarget,
  getSecondaryTarget,
  getObjects,
  getTargets,
  getSecondaryTargets,
  getOfType,
  getTargetTypes,
  isTargetOfType,
  getTargetOfType,
  hasTargetsOfType,
  getTargetsOfType,
  getSecondaryTargetTypes,
  isSecondaryTargetOfType,
  getSecondaryTargetOfType,
  hasSecondaryTargetsOfType,
  getSecondaryTargetsOfType,
  getObjectTypes,
  isObjectOfType,
  getObjectOfType,
  hasObjectsOfType,
  getObjectsOfType,
  CLIENT,
  SCOPE,
  USE,
  MODE,
  TARGET,
  SECONDARY_TARGET,
  CONDITION_FACTORY,
  SATISFIES_FACTORY,
  PERFORMS_FACTORY,
  ACTION_FACTORY,
  IMAGINE_API
}
